import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { resolvePath, useLocation, useNavigate } from "react-router";

import type { ReactNode } from "react";
import type { NavigateOptions, To } from "react-router";

export const STALE_NAVIGATION_THRESHOLD_MS = 24 * 60 * 60 * 1000;
export const STALE_NAVIGATION_SKIP_ATTRIBUTE = "data-stale-navigation-skip";
const DEFAULT_CLIENT_BOOTED_AT_MS = typeof document === "undefined" ? 0 : Date.now();

type StaleNavigationContextValue = {
  isStaleNavigation: () => boolean;
};

type HardNavigationAnchorClickArgs = {
  anchor: HTMLAnchorElement;
  currentUrl: URL;
  event: MouseEvent;
  isStale: boolean;
};

const DEFAULT_STALE_NAVIGATION_CONTEXT: StaleNavigationContextValue = {
  isStaleNavigation: () => false,
};

const StaleNavigationContext = createContext<StaleNavigationContextValue>(
  DEFAULT_STALE_NAVIGATION_CONTEXT,
);

export function isStaleNavigation(bootedAtMs: number, nowMs = Date.now()): boolean {
  return nowMs - bootedAtMs >= STALE_NAVIGATION_THRESHOLD_MS;
}

export function shouldHardNavigatePathname({
  currentPathname,
  nextPathname,
  isStale,
}: {
  currentPathname: string;
  nextPathname: string;
  isStale: boolean;
}): boolean {
  return isStale && currentPathname !== nextPathname;
}

export function getHardNavigationUrlForAnchorClick({
  anchor,
  currentUrl,
  event,
  isStale,
}: HardNavigationAnchorClickArgs): string | null {
  if (
    event.defaultPrevented ||
    !isStale ||
    event.button !== 0 ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
  ) {
    return null;
  }

  if (anchor.closest(`[${STALE_NAVIGATION_SKIP_ATTRIBUTE}]`) != null) {
    return null;
  }

  if (anchor.hasAttribute("download")) {
    return null;
  }

  const target = anchor.getAttribute("target");
  if (target != null && target !== "" && target.toLowerCase() !== "_self") {
    return null;
  }

  const nextUrl = new URL(anchor.href, currentUrl.href);
  if (nextUrl.origin !== currentUrl.origin) {
    return null;
  }

  if (
    !shouldHardNavigatePathname({
      currentPathname: currentUrl.pathname,
      nextPathname: nextUrl.pathname,
      isStale,
    })
  ) {
    return null;
  }

  return nextUrl.toString();
}

export function StaleNavigationProvider({
  children,
  bootedAtMs,
}: {
  children: ReactNode;
  bootedAtMs?: number;
}) {
  const bootedAtRef = useRef(bootedAtMs ?? DEFAULT_CLIENT_BOOTED_AT_MS);
  const isTabStale = useCallback(() => isStaleNavigation(bootedAtRef.current), []);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }

      const hardNavigationUrl = getHardNavigationUrlForAnchorClick({
        anchor,
        currentUrl: new URL(window.location.href),
        event,
        isStale: isTabStale(),
      });

      if (hardNavigationUrl == null) {
        return;
      }

      event.preventDefault();
      window.location.assign(hardNavigationUrl);
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [isTabStale]);

  const value = useMemo<StaleNavigationContextValue>(
    () => ({
      isStaleNavigation: isTabStale,
    }),
    [isTabStale],
  );

  return (
    <StaleNavigationContext.Provider value={value}>{children}</StaleNavigationContext.Provider>
  );
}

export function useStaleAwareNavigate() {
  const { isStaleNavigation: isTabStale } = useContext(StaleNavigationContext);
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        navigate(to);
        return;
      }

      const nextPath = resolvePath(to, location.pathname);

      if (
        shouldHardNavigatePathname({
          currentPathname: location.pathname,
          nextPathname: nextPath.pathname,
          isStale: isTabStale(),
        })
      ) {
        const nextUrl = new URL(nextPath.pathname, window.location.origin);
        nextUrl.search = nextPath.search;
        nextUrl.hash = nextPath.hash;
        window.location.assign(nextUrl.toString());
        return;
      }

      if (options == null) {
        navigate(to);
        return;
      }

      navigate(to, options);
    },
    [isTabStale, location.pathname, navigate],
  );
}
